import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Gavel as GavelIcon,
  MoneyOff as MoneyOffIcon,
  Groups as GroupsIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";

const DisclaimerPage: React.FC = () => {
  const lastUpdated = "January 8, 2026";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: "url('/leaves-removebg-preview.png')",
        backgroundBlendMode: "soft-light",
      }}
    >
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <WarningIcon sx={{ fontSize: 48, color: "warning.main", mb: 2 }} />
            <Typography
              variant="h3"
              component="h1"
              fontWeight={700}
              gutterBottom
            >
              Disclaimer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last Updated: {lastUpdated}
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body1" paragraph>
            Please read this disclaimer carefully before using the Sanduuq
            lending circle platform. By accessing or using our service, you
            acknowledge that you have read, understood, and agree to be bound by
            this disclaimer.
          </Typography>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              No Financial Advice
            </Typography>
            <Typography variant="body1" paragraph>
              Sanduuq is a platform that facilitates peer-to-peer rotating
              savings and credit associations (ROSCAs), commonly known as
              lending circles. We do not provide financial, investment, legal,
              or tax advice. The information and services provided through our
              platform are for informational purposes only and should not be
              construed as professional financial advice.
            </Typography>
            <Typography variant="body1" paragraph>
              Before participating in any lending circle, we strongly recommend
              consulting with a qualified financial advisor, accountant, or
              legal professional to understand the implications for your
              specific situation.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Risk Acknowledgment
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <MoneyOffIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="Financial Risk"
                  secondary="Participation in lending circles involves financial risk. There is no guarantee that all members will fulfill their payment obligations. You may lose some or all of your contributed funds."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <GroupsIcon color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary="Member Default Risk"
                  secondary="If a circle member defaults on their payments after receiving their payout, remaining members may not receive the full expected amount. Sanduuq is not responsible for member defaults."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SecurityIcon color="info" />
                </ListItemIcon>
                <ListItemText
                  primary="Platform Risk"
                  secondary="While we implement security measures, no system is completely secure. Technical issues, system failures, or cyberattacks could potentially affect your transactions or data."
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Platform Role
            </Typography>
            <Typography variant="body1" paragraph>
              Sanduuq serves solely as a technology platform to facilitate the
              organization and management of lending circles. We are not:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• A bank or financial institution" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• A lender or borrower in any circle" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• An insurer or guarantor of any funds" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• A party to agreements between circle members" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              No Guarantees
            </Typography>
            <Typography variant="body1" paragraph>
              We do not guarantee:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• The performance or reliability of circle members" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• The recovery of funds in case of default" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Continuous, uninterrupted access to the platform" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• The accuracy of information provided by other users" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              To the fullest extent permitted by applicable law, Sanduuq and its
              officers, directors, employees, agents, and affiliates shall not
              be liable for any indirect, incidental, special, consequential, or
              punitive damages, including but not limited to loss of profits,
              data, or other intangible losses, resulting from:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Your use or inability to use the service" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Any conduct or content of other users" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Unauthorized access to your account or data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Default by any circle member" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Your Responsibility
            </Typography>
            <Typography variant="body1" paragraph>
              By using Sanduuq, you acknowledge that you:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Are solely responsible for your decision to participate in lending circles" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Have assessed the risks and are financially able to bear potential losses" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Will only participate with funds you can afford to lose" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Understand the terms of any circle you join" />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <GavelIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              This disclaimer is subject to change. Continued use of the
              platform constitutes acceptance of any modifications.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DisclaimerPage;
